import { useState } from "react";
import { Medal } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * 3rd-place playoff (bronze medal match) scoring toggle.
 *
 * Default OFF — most tournaments treat the bronze game as a consolation that
 * shouldn't shift the leaderboard (WC, CFP, NFL, NCAAM). Olympics admins
 * should flip this ON since bronze actually matters.
 *
 * When OFF the ESPN sync ignores the bronze match entirely (no schedule row,
 * no stats, no stage change). When ON the match is processed against the
 * "final" stage — winner's stage_reached stays at "sf" but their group_wins
 * / group_for / group_against increment normally.
 */
export default function ThirdPlacePlayoffCard({ tournament, isLocked, onChanged }) {
    const [saving, setSaving] = useState(false);
    if (!tournament) return null;
    const enabled = !!tournament.count_third_place_playoff;

    const handleToggle = async () => {
        if (isLocked) return;
        setSaving(true);
        try {
            await api.setCountThirdPlace(tournament.id, !enabled);
            toast.success(
                !enabled
                    ? "3rd-place playoff will now be SCORED. Click Refresh in Live Sync if the match was previously synced."
                    : "3rd-place playoff will now be IGNORED. Existing bronze-match data in the DB is unaffected — click Refresh in Live Sync to fully re-sync."
            );
            onChanged?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Toggle failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="brutal-card p-5 space-y-3" data-testid="third-place-toggle-card">
            <div className="flex items-center gap-2">
                <Medal className="w-5 h-5 text-cyber" />
                <h2 className="font-display font-extrabold text-xl uppercase">
                    Score the 3rd-place playoff?
                </h2>
            </div>
            <p className="text-xs text-zinc-400 leading-relaxed">
                Most tournaments (WC, NFL, CFP, NCAAM) treat the bronze medal match as a
                consolation game — both losing semifinalists already earned the "reached
                semifinals" bonus, so the 3rd-place game shouldn't move the leaderboard.
                {" "}<strong className="text-white">Default: OFF.</strong>
                {" "}Flip it ON for events like the Olympics where bronze genuinely matters.
            </p>
            <div className="flex items-center justify-between gap-3 pt-1">
                <p className="text-sm">
                    Current setting:{" "}
                    <strong
                        data-testid="third-place-toggle-state"
                        className={enabled ? "text-cyber" : "text-zinc-500"}
                    >
                        {enabled ? "ON — bronze game scores" : "OFF — bronze game ignored"}
                    </strong>
                </p>
                <button
                    data-testid="third-place-toggle-btn"
                    onClick={handleToggle}
                    disabled={isLocked || saving}
                    className={`brutal-btn text-xs px-4 py-2 ${enabled ? "border-zinc-600 text-zinc-300" : "bg-cyber text-black"} disabled:opacity-40`}
                >
                    {saving ? "Saving…" : enabled ? "Turn OFF" : "Turn ON"}
                </button>
            </div>
        </div>
    );
}
